import { Loader } from '../Loader';
import { runtime } from '../Runtime';

const clamp = (value, min = 0, max = 100) => Math.max(min, Math.min(max, value));

const updatePreloaderProgress = (loader, value) => {
    const progress = clamp(value);
    const rounded = Math.round(progress);

    loader.style.setProperty('--zero-un-preloader-progress', `${progress}%`);
    loader.style.setProperty('--zero-un-preloader-progress-ratio', String(progress / 100));

    loader.querySelectorAll('[data-preloader-percent]').forEach((node) => {
        node.textContent = `${rounded}%`;
    });
};

const createPreloaderLoops = ({ gsap, loader }) => {
    const loops = [];

    if(loader.classList.contains('zero-un-preloader--bounce-logo')){
        const brand = loader.querySelector('.zero-un-preloader__brand');

        if(brand){
            const brandMotion = brand.querySelector('.zero-un-preloader__brand-svg, .zero-un-preloader__brand-text') || brand;

            gsap.set(brand, {
                y: 0,
                scale: 1,
                clearProps: 'transform,transformOrigin,willChange'
            });

            gsap.set(brandMotion, {
                y: 0,
                scale: 1,
                transformOrigin: '50% 50%',
                willChange: 'transform',
                force3D: true
            });

            loops.push(
                gsap.timeline({
                    defaults: {
                        duration: 0.34,
                        ease: 'power1.inOut',
                        overwrite: false
                    }
                })
                .to(brandMotion, {
                    scale: 1.1
                })
                .to(brandMotion, {
                    scale: 0.9,
                    repeat: -1,
                    yoyo: true
                })
            );
        }
    }

    return loops;
};

const killPreloaderLoops = ({ gsap, loader, loops = [] }) => {
    const loopTargets = Array.from(loader.querySelectorAll('.zero-un-preloader__brand, .zero-un-preloader__brand-svg, .zero-un-preloader__brand-text'));

    loops.forEach((loop) => {
        loop?.kill?.();
    });

    gsap.killTweensOf(loopTargets);
    gsap.set(loopTargets, {
        y: 0,
        scale: 1,
        clearProps: 'transform,transformOrigin,willChange'
    });
};

export const configureLoader = () => {
    Loader.setAnimation(({ gsap, ScrollTrigger, done }) => {
        const loader = document.getElementById('loader');
        const config = runtime.system?.zeroUn?.preloader || {};
        const motion = config.motion || {};

        if(!loader || config.enabled === false){
            if(loader){
                loader.classList.add('is-complete');
                gsap.set(loader, {
                    autoAlpha: 0,
                    pointerEvents: 'none',
                    display: 'none'
                });
            }

            done();
            return null;
        }

        return new Promise((resolve) => {
            let progress = 0;
            let raf = 0;
            let finished = false;
            const loops = createPreloaderLoops({
                gsap,
                loader
            });
            const startTime = performance.now();
            const minimumDuration = Number.isFinite(Number(motion.minimumDuration)) ? Number(motion.minimumDuration) : 0.8;
            const exitDuration = Number.isFinite(Number(motion.exitDuration)) ? Number(motion.exitDuration) : 0.65;
            const smoothing = Number.isFinite(Number(motion.progressSmoothing)) ? Number(motion.progressSmoothing) : 8;
            const easing = clamp(smoothing, 1, 30) / 100;
            const isSwipePanel = loader.classList.contains('zero-un-preloader--swipe-panel');
            const finish = () => {
                if(finished){
                    return;
                }

                finished = true;
                window.cancelAnimationFrame(raf);
                updatePreloaderProgress(loader, 100);
                loader.classList.add('is-complete');
                loader.querySelectorAll('svg').forEach((svg) => {
                    svg.pauseAnimations?.();
                });

                gsap.to(loader, {
                    duration: exitDuration,
                    autoAlpha: 0,
                    pointerEvents: 'none',
                    ease: 'power2.inOut',
                    overwrite: false,
                    onStart: () => {
                        ScrollTrigger?.refresh();
                    },
                    onComplete: () => {
                        killPreloaderLoops({
                            gsap,
                            loader,
                            loops
                        });
                        gsap.killTweensOf(loader);
                        gsap.set(loader, {
                            display: 'none'
                        });
                        resolve();
                    }
                });
            };

            if(isSwipePanel){
                const panelState = { progress: 0 };
                const panelRevealDuration = Number.isFinite(Number(motion.panelRevealDuration))
                    ? Number(motion.panelRevealDuration)
                    : 0.45;
                const panelRevealDelay = Number.isFinite(Number(motion.panelRevealDelay))
                    ? Number(motion.panelRevealDelay)
                    : 0.4;
                const revealDelay = Math.max(0.4, panelRevealDelay || 0.4);
                const revealDuration = Math.max(0.15, panelRevealDuration || 0.45);
                let panelStarted = false;

                updatePreloaderProgress(loader, 0);

                const startPanelReveal = () => {
                    if(panelStarted){
                        return;
                    }

                    panelStarted = true;

                    gsap.to(panelState, {
                        progress: 100,
                        duration: revealDuration,
                        overwrite: false,
                        onUpdate: () => updatePreloaderProgress(loader, panelState.progress),
                        onComplete: () => {
                            updatePreloaderProgress(loader, 100);
                            finish();
                        }
                    });
                };

                const waitForPanelAndLoad = () => {
                    const elapsed = (performance.now() - startTime) / 1000;
                    const canStartReveal = Boolean(window.loader?.isLoaded) && elapsed >= revealDelay;

                    if(canStartReveal){
                        startPanelReveal();
                        return;
                    }

                    raf = window.requestAnimationFrame(waitForPanelAndLoad);
                };

                raf = window.requestAnimationFrame(waitForPanelAndLoad);
                return;
            }

            const tick = () => {
                const elapsed = (performance.now() - startTime) / 1000;
                const canFinish = Boolean(window.loader?.isLoaded) && elapsed >= minimumDuration;
                const target = canFinish
                    ? 100
                    : clamp(22 + (elapsed * 28), 0, 92);

                progress += (target - progress) * easing;
                updatePreloaderProgress(loader, progress);

                if(canFinish && progress >= 99.4){
                    finish();
                    return;
                }

                raf = window.requestAnimationFrame(tick);
            };

            updatePreloaderProgress(loader, 0);
            raf = window.requestAnimationFrame(tick);
        }).then(() => {
            done();
        });
    }, ({ gsap, done }) => {
        const loader = document.getElementById('loader');

        if(loader){
            updatePreloaderProgress(loader, 100);
            loader.classList.add('is-complete');
            loader.querySelectorAll('svg').forEach((svg) => {
                svg.pauseAnimations?.();
            });
            gsap.set(loader, {
                autoAlpha: 0,
                pointerEvents: 'none',
                display: 'none'
            });
        }

        done();
        return null;
    });
};
